import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Hand, Sparkles, ShieldAlert, ChevronLeft, X, Webhook, Trophy, Users } from 'lucide-react';

interface SidebarProps {
  guildId: string;
  guildName: string;
  guildIcon: string | null;
  open: boolean;
  onClose: () => void;
}

const navItems = [
  { id: 'overview',    label: 'Overview',    icon: LayoutDashboard, path: '',          group: 'main' },
  { id: 'welcome',     label: 'Welcome',     icon: Hand,            path: '/welcome',  group: 'config' },
  { id: 'moderation',  label: 'Moderation',  icon: ShieldAlert,     path: '/moderation', group: 'config' },
  { id: 'pokemon',     label: 'Pokémon',     icon: Sparkles,        path: '/pokemon',  group: 'config' },
  { id: 'roles',       label: 'Members',    icon: Users,           path: '/roles',    group: 'config' },
  { id: 'leveling',    label: 'Leveling',    icon: Trophy,          path: '/leveling', group: 'config' },
  { id: 'webhooks',    label: 'Webhooks',    icon: Webhook,         path: '/webhooks', group: 'tools' },
];

const groups = [
  { id: 'main',   label: null },
  { id: 'config', label: 'Configuration' },
  { id: 'tools',  label: 'Tools' },
];

export default function Sidebar({ guildId, guildName, guildIcon, open, onClose }: SidebarProps) {
  const location = useLocation();
  const basePath = `/dashboard/${guildId}`;

  function isActive(path: string) {
    if (path === '') {
      return location.pathname === basePath || location.pathname === basePath + '/';
    }
    return location.pathname.startsWith(basePath + path);
  }

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Server header */}
      <div className="px-3 pt-4 pb-3">
        {/* Back link */}
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 text-gray-600 hover:text-gray-400 text-[11px] transition-colors mb-3 group"
        >
          <ChevronLeft className="w-3 h-3 group-hover:-translate-x-0.5 transition-transform" />
          All servers
        </Link>

        {/* Server identity card */}
        <div className="flex items-center gap-2.5 px-2.5 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.05]">
          {guildIcon ? (
            <img src={guildIcon} alt={guildName} className="w-8 h-8 rounded-lg shrink-0" />
          ) : (
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/30 to-secondary/30 flex items-center justify-center text-white font-bold text-xs shrink-0">
              {guildName.charAt(0)}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-white font-semibold text-[12px] truncate leading-tight">{guildName}</p>
            <p className="text-gray-600 text-[10px] mt-0.5 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
              Connected
            </p>
          </div>
          <button onClick={onClose} className="lg:hidden p-1 text-gray-600 hover:text-white transition-colors shrink-0">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 pb-4 space-y-0.5 overflow-y-auto">
        {groups.map((group) => {
          const items = navItems.filter((i) => i.group === group.id);
          return (
            <div key={group.id} className={group.label ? 'mt-3' : ''}>
              {group.label && (
                <p className="px-2.5 mb-1.5 text-[10px] font-semibold text-gray-700 uppercase tracking-wider">
                  {group.label}
                </p>
              )}
              {items.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.id}
                    to={`${basePath}${item.path}`}
                    onClick={onClose}
                    className={`
                      relative flex items-center gap-2.5 px-3 py-2 rounded-xl text-[13px] font-medium
                      transition-all duration-150 group
                      ${active
                        ? 'text-white'
                        : 'text-gray-500 hover:bg-white/[0.03] hover:text-gray-300'
                      }
                    `}
                  >
                    {/* Active background */}
                    {active && (
                      <span className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary/15 to-secondary/10 border border-primary/20" />
                    )}
                    <Icon className={`w-[15px] h-[15px] shrink-0 relative z-10 transition-colors ${active ? 'text-primary' : 'group-hover:text-gray-400'}`} />
                    <span className="relative z-10">{item.label}</span>
                    {active && (
                      <span className="ml-auto relative z-10 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                    )}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* Bottom branding */}
      <div className="px-3 pb-4 pt-2 border-t border-white/[0.04]">
        <div className="flex items-center gap-2 px-2">
          <div className="w-5 h-5 rounded-md bg-gradient-to-br from-primary/30 to-secondary/30 flex items-center justify-center">
            <Sparkles className="w-3 h-3 text-primary" />
          </div>
          <span className="text-[11px] text-gray-700 font-medium">Anya Dashboard</span>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile: overlay backdrop */}
      {open && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Mobile sidebar */}
      <aside
        className={`
          fixed top-[72px] left-0 bottom-0 w-56 z-50
          bg-dark-900 border-r border-white/[0.06]
          transition-transform duration-200 ease-out
          lg:hidden
          ${open ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {sidebarContent}
      </aside>

      {/* Desktop static sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:w-56 lg:shrink-0 lg:sticky lg:top-[72px] lg:h-[calc(100vh-72px)] border-r border-white/[0.05] bg-dark-900/60">
        {sidebarContent}
      </aside>
    </>
  );
}
